import { Eye, EyeOff } from 'react-feather';
import { isFuture, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';

interface Discount {
  start_date?: string | null; // UTC
  end_date?: string | null; // UTC
  active: boolean;
}

export interface DiscountStatusIndicatorProps {
  discount: Discount;
}

const DiscountStatusIndicator = ({ discount }: DiscountStatusIndicatorProps) => {
  const { status, message } = useDiscountStatus(discount);

  const icons = {
    [DiscountStatus.Available]: <Eye />,
    [DiscountStatus.Disabled]: <EyeOff />,
    [DiscountStatus.StartsOn]: <EyeOff />,
    [DiscountStatus.EndsOn]: <Eye />,
    [DiscountStatus.EndedOn]: <EyeOff />,
  };

  const icon = icons[status];

  return (
    <>
      <UI.Icon>
        {icon}
      </UI.Icon>
      {' '}
      {message}
    </>
  );
};

export enum DiscountStatus {
  Available = 'available',
  Disabled = 'disabled',
  StartsOn = 'starts_on',
  EndsOn = 'ends_on',
  EndedOn = 'ended_on',
}

export const useDiscountStatus = (discount: Discount) => {
  const { t } = useTranslation();
  const { parseDate, formatDate } = useLocale();

  const status = (status: DiscountStatus, message: string, date: string = undefined) => ({
    status, message, date,
  });

  if (!discount.active) {
    return status(DiscountStatus.Disabled, t('disabled'));
  }

  if (discount.start_date) {
    const startDate = parseDate(discount.start_date, { timezone: 'UTC' });

    if (isFuture(startDate)) {
      const date = formatDate(
        startDate,
        { format: isToday(startDate) ? 'display_time' : 'display_compact_date' },
      );

      return status(DiscountStatus.StartsOn, t('starts_on', { date }), date);
    }
  }

  if (discount.end_date) {
    const endDate = parseDate(discount.end_date, { timezone: 'UTC' });

    const date = formatDate(
      endDate,
      { format: isToday(endDate) ? 'display_time' : 'display_compact_date' },
    );

    if (isFuture(endDate)) {
      return status(DiscountStatus.EndsOn, t('ends_on_date', { date }), date);
    }

    return status(DiscountStatus.EndedOn, t('ended_on', { date }), date);
  }

  return status(DiscountStatus.Available, t('available'));
};

export default DiscountStatusIndicator;
